///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Pure geometry for the storm column: how big the block of atmosphere is,
/// which way it points and how much the height axis is stretched. The
/// block frame is x along the storm's motion, y up (km, y = 0 at the sea
/// surface), z across motion (right-handed with y up, so +z is to the
/// right of the motion). Local km east/north about the storm center via the
/// equirectangular helpers shared with the earthquake cutout.
///

import { clamp, localOffsetKm, localToLatLon, toBlockXZ, fromBlockXZ } from "@/earthbrowser/dataset-distribution/earthquakes/cutout/cutoutLayout"
import { type StormProperties, type StormTrackPoint, type WindRadii } from "../HurricanesTable"

export { localOffsetKm, localToLatLon, toBlockXZ, fromBlockXZ, clamp }

const cKmPerDegree = 111.32
const cKmPerNm = 1.852

/**
 * Top of the box: the tropical tropopause, true km. Nothing in the frames
 * can read above the 200 K encoding floor (≈ 15.4 km), so there is no
 * headroom to spare above this
 */
export const cColumnHeightKm = 17
/** Height grid spacing, true km */
export const cColumnTickKm = 3

/** Standard pressure levels drawn on the across-motion wall (ICAO standard atmosphere heights) */
export const cPressureLevels: Array<{ label: string; km: number }> = [
	{ label: "850 hPa", km: 1.5 },
	{ label: "500 hPa", km: 5.6 },
	{ label: "200 hPa", km: 11.8 },
	{ label: "100 hPa", km: 16.2 },
]

export interface StormColumnLayout {
	/** Motion direction, radians clockwise from north: the block's +x axis */
	strike: number
	/** Block center (the advisory position), degrees */
	centerLat: number
	centerLon: number
	/** Block extent along (x) and across (z) the motion, km */
	lengthKm: number
	widthKm: number
	/** Top of the box, true km */
	heightKm: number
	tickKm: number
	/**
	 * Vertical stretch of the height axis (integer, printed on the wall).
	 * 16 km of troposphere against a ~1500 km footprint is a sheet; the
	 * stretch keeps the eyewall tower and the wall sections legible. Aimed
	 * at a box ~15% as tall as it is long (half the quake block's ratio):
	 * the storm's information is in the top few km of relief, not in the
	 * empty air above it, so a taller box only hides the globe
	 */
	heightScale: number
	/** Clearance between the sea-surface plane and the globe, km */
	hoverKm: number
}

/** Box height in block-frame km (height axis after stretching) */
export function boxHeightKm(layout: StormColumnLayout): number {
	return layout.heightKm * layout.heightScale
}

/** Lat/lon box of a storm-centered IR frame, degrees */
export interface FrameBox {
	minLon: number
	minLat: number
	maxLon: number
	maxLat: number
}

/** Largest 34-kt radius in the advisory, km; undefined without radii */
export function maxRadius34Km(radii: WindRadii | null): number | undefined {
	const q = radii?.["34"]
	if (!q) return undefined
	const nm = Math.max(q.ne, q.se, q.sw, q.nw)
	return nm > 0 ? nm * cKmPerNm : undefined
}

/**
 * Motion direction for the block axis: the advisory's movement when
 * present, else the bearing between the last two observed positions, else
 * north
 */
export function motionDirectionRad(storm: Pick<StormProperties, "movement_dir">, observed: StormTrackPoint[]): number {
	if (storm.movement_dir !== null && Number.isFinite(storm.movement_dir)) {
		return storm.movement_dir * Math.PI / 180
	}
	if (observed.length >= 2) {
		const a = observed[observed.length - 2]
		const b = observed[observed.length - 1]
		const d = localOffsetKm(b.lat, b.lon, a.lat, a.lon)
		if (Math.hypot(d.east, d.north) > 1) return Math.atan2(d.east, d.north)
	}
	return 0
}

/**
 * Size and orient the block around a storm. The footprint is a few 34-kt
 * radii across (or a default for storms without radii), kept inside the IR
 * frame's box when one exists so the lid is never mostly empty, and the
 * height axis is stretched until the box is ~15% as tall as it is long
 */
export function buildStormColumnLayout(storm: StormProperties, radii: WindRadii | null, observed: StormTrackPoint[], frame?: FrameBox): StormColumnLayout {
	const strike = motionDirectionRad(storm, observed)
	const r34 = maxRadius34Km(radii)
	let halfLength = r34 !== undefined ? clamp(3 * r34, 350, 800) : 550
	let halfWidth = 0.75 * halfLength

	// Keep the rotated footprint inside the frame's inscribed circle
	if (frame) {
		const halfNs = (frame.maxLat - frame.minLat) / 2 * cKmPerDegree
		const halfEw = (frame.maxLon - frame.minLon) / 2 * cKmPerDegree * Math.cos(storm.lat * Math.PI / 180)
		const inscribed = Math.min(halfNs, halfEw)
		const halfDiag = Math.hypot(halfLength, halfWidth)
		if (halfDiag > inscribed && inscribed > 100) {
			const s = inscribed / halfDiag
			halfLength *= s
			halfWidth *= s
		}
	}

	const lengthKm = Math.round(halfLength * 2)
	const widthKm = Math.round(halfWidth * 2)
	const heightScale = clamp(Math.round(0.15 * lengthKm / cColumnHeightKm), 4, 30)
	const hoverKm = clamp(0.08 * lengthKm, 10, 60)

	return {
		strike,
		centerLat: storm.lat,
		centerLon: storm.lon,
		lengthKm,
		widthKm,
		heightKm: cColumnHeightKm,
		tickKm: cColumnTickKm,
		heightScale,
		hoverKm
	}
}

/** Block-frame x/z (km) of a lat/lon */
export function latLonToBlock(layout: StormColumnLayout, lat: number, lon: number): { x: number; z: number } {
	const local = localOffsetKm(lat, lon, layout.centerLat, layout.centerLon)
	return toBlockXZ(local.east, local.north, layout.strike)
}

/** Lat/lon (degrees) of a block-frame x/z */
export function blockToLatLon(layout: StormColumnLayout, x: number, z: number): { lat: number; lon: number } {
	const local = fromBlockXZ(x, z, layout.strike)
	return localToLatLon(local.east, local.north, layout.centerLat, layout.centerLon)
}

/** True when a block-frame point lies inside the footprint */
export function insideColumn(layout: StormColumnLayout, x: number, z: number, marginKm: number = 0): boolean {
	return Math.abs(x) <= layout.lengthKm / 2 + marginKm && Math.abs(z) <= layout.widthKm / 2 + marginKm
}

/**
 * Compass bearing (radians clockwise from north) of a block-frame point
 * from the block center: the +x axis is the motion direction and +z lies
 * 90° clockwise of it
 */
export function blockBearingRad(layout: StormColumnLayout, x: number, z: number): number {
	const b = layout.strike + Math.atan2(z, x)
	return ((b % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)
}
